import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { IApiQueryOptions } from 'api/ApiClient';
import { Card, PaginatedList, PaymentOverviewTable, Section } from 'components';
import { PayoutStats } from 'components/Stats/PayoutStats';
import { theme, toRem } from 'design';
import { useQueryString } from 'hooks/useQueryString';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCurrentDateRange } from 'store/App';
import { getSitePaymentsRequest, Payments, selectPaymentSetsForSite, selectTotalCountPaymentSetsForSite } from 'store/Collections/Payments';
import { selectCurrentEmployeeSite } from 'store/Employers';
import { getSitePayoutStatsRequest, selectPayoutStats, Stats } from 'store/Stats';
import { IStore } from 'store/types';
import { ApiQueryOperator, IApiQueryParam } from 'types';
import { formatDateForApi } from 'utils';
import { sumBy } from 'utils/arrays';
import { DEFAULT_PAGE_SIZE } from 'utils/table';
import BusinessDaySection from './BusinessDaySection';
import OverviewTabChart from './OverviewTabChart';
import { ChartSectionHeader } from './styles';

const StyledSection = styled(Section)`
  padding: 0 0 0 ${toRem(theme.space.xl)};
  z-index: 100;

  > div {
    padding-right: ${toRem(theme.space.xl)};
  }

  @media (min-width: ${theme.breakpoint.med}px) {
    padding: 0 0 0 ${toRem(theme.space['3xl'])};

    > div {
      padding-right: ${toRem(theme.space['3xl'])};
    }

    &.chart-section {
      margin-top: ${toRem(-theme.space['4xl'])};
    }
  }
`;

const ChartCard = styled(Card)`
  flex-flow: column nowrap;
  overflow: visible;
`;

const StatsContainer = styled.div`
  margin-top: ${toRem(theme.space['2xl'])};
  width: 100%;
`;

export const OverviewTab = () => {
  const dispatch = useDispatch();

  const currentSite = useSelector(selectCurrentEmployeeSite);
  const currentDateRange = useSelector(selectCurrentDateRange());
  const paymentSets = useSelector(selectPaymentSetsForSite());
  const payoutStats = useSelector(selectPayoutStats());

  const totalCountPaymentSetsSelector = useSelector(selectTotalCountPaymentSetsForSite());
  const isPaymentsLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payments.GET_SITE_PAYMENTS_REQUEST));
  const isStatsLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Stats.GET_SITE_PAYOUT_STATS_REQUEST));

  const [urlQuery, setUrlQuery] = useQueryString('paymentsOverview', window.location, {
    sort: '-payment_date',
    limit: DEFAULT_PAGE_SIZE,
    offset: 0,
    q: [
      { key: 'payment_date', operator: ApiQueryOperator.gte, value: currentDateRange.startDate },
      { key: 'payment_date', operator: ApiQueryOperator.lte, value: currentDateRange.endDate },
    ],
  });

  const paymentsByDateCountTotal = payoutStats ? sumBy(payoutStats.byDate, 'count') : '--';

  const onQueryChange = (options: IApiQueryOptions) => setUrlQuery(options);

  const onSortChange = (newSort: string) => setUrlQuery({ sort: urlQuery.sort === newSort ? `-${urlQuery.sort}` : newSort });

  const onDateRangeChange = (startDate: string, endDate: string) => {
    const query = (urlQuery.q || []).filter((item: IApiQueryParam) => item.key !== 'payment_date');
    query.push({ key: 'payment_date', operator: ApiQueryOperator.gte, value: startDate });
    query.push({ key: 'payment_date', operator: ApiQueryOperator.lte, value: endDate });
    onQueryChange({ ...urlQuery, q: query, offset: 0 });
  };

  // Fetch payments when the url query changes
  useEffect(() => {
    if (currentSite) {
      dispatch(getSitePaymentsRequest(currentSite.id, urlQuery));
    }
  }, [dispatch, urlQuery, currentSite?.id]);

  // Fetch stats when the date range changes
  useEffect(() => {
    if (currentSite && currentDateRange.startDate && currentDateRange.endDate) {
      dispatch(getSitePayoutStatsRequest(currentSite.id, currentDateRange.startDate, currentDateRange.endDate));
    }
  }, [dispatch, currentSite?.id, currentDateRange]);

  return (
    <>
      <StyledSection className="chart-section" noContentBorder data-cy="payments-chart-section">
        <ChartCard>
          <ChartSectionHeader title="Payments" value={`(${paymentsByDateCountTotal})`} isLoading={isPaymentsLoading}>
            <BusinessDaySection isLoading={isPaymentsLoading} endDate={currentDateRange.endDate} startDate={currentDateRange.startDate} setDates={(dates) => onDateRangeChange(formatDateForApi(dates[0]), formatDateForApi(dates[1]))} />
          </ChartSectionHeader>

          <OverviewTabChart paymentStats={payoutStats?.byDate} isLoading={isStatsLoading} />
          <StatsContainer>
            <PayoutStats isLoading={isStatsLoading} stats={payoutStats} />
          </StatsContainer>
        </ChartCard>
      </StyledSection>

      <StyledSection data-cy="table">
        <PaginatedList total={totalCountPaymentSetsSelector} limit={urlQuery?.limit} offset={urlQuery?.offset} onPageChange={onQueryChange} isLoading={isPaymentsLoading} standalone={false}>
          <PaymentOverviewTable isLoading={isPaymentsLoading} paymentSets={paymentSets} onSortChange={onSortChange} urlQuery={urlQuery} />
        </PaginatedList>
      </StyledSection>
    </>
  );
};
